import * as React from 'react';

import moment from 'moment';

import DatepickerFilters from './datepicker-filters';
import DatepickerYears from '../datepicker/datepicker-years';
import DatepickerControls from '../datepicker/datepicker-controls';
import DatepickerCalendar from '../datepicker/datepicker-calendar';

interface DatepickerWithFiltersProps {
  className?: any;
  value?: string;
  active?: moment.Moment;
  min?: moment.Moment;
  max?: moment.Moment;
  start?: moment.Moment;
  end?: moment.Moment;
  removeFilters?: boolean;
  filters?: { label: string; value: number }[];
  hasChanges?: boolean;
  onSelectedYear?: () => void;
  onChange: (start: moment.Moment, end?: moment.Moment) => void;
}

const ONE_DAY_OFFSET = 24 * 60 * 60 * 1000;

const FILTERS = [
  { label: '1 Day', value: ONE_DAY_OFFSET },
  { label: '7 Days', value: 7 * ONE_DAY_OFFSET },
  { label: '1 Month', value: 30 * ONE_DAY_OFFSET },
  { label: '2 Month', value: 60 * ONE_DAY_OFFSET },
  { label: '3 Month', value: 90 * ONE_DAY_OFFSET },
  { label: '6 Month', value: 180 * ONE_DAY_OFFSET },
  { label: '1 Year', value: 365 * ONE_DAY_OFFSET },
];

export const DatepickerWithFilters: React.FC<DatepickerWithFiltersProps> = ({
  min = moment().subtract(100, 'years'),
  max = moment().add(2, 'years'),
  ...props
}) => {
  const activeMoment = moment(new Date()).startOf('month');

  const [active, setActive] = React.useState(activeMoment);
  const [activeFilter, setActiveFilter] = React.useState<number>();

  const [start, setStart] = React.useState<moment.Moment | undefined>(
    props.start || undefined
  );
  const [end, setEnd] = React.useState<moment.Moment | undefined>(
    props.end || undefined
  );

  const [isYearMenuOpen, setIsYearMenuOpen] = React.useState(false);

  const moveNextMonth = () => setActive(moment(active).add(1, 'month'));
  const movePrevMonth = () => setActive(moment(active).subtract(1, 'month'));

  const handleChange = (
    newStart: moment.Moment,
    newEnd?: moment.Moment,
    resetFilter?: boolean
  ) => {
    setStart(newStart);
    setEnd(newEnd);

    if (resetFilter) {
      setActiveFilter(undefined);
    }

    props.onChange(newStart, newEnd);
  };

  const handleFilter = (value: number) => {
    setActiveFilter(value);
    const now = new Date();

    handleChange(
      moment(now).subtract(value, 'milliseconds').startOf('day'),
      moment(now).subtract(1, 'day').endOf('day')
    );
  };

  const handleYear = (year: number) => {
    const newDate = moment(active).set('year', year);
    setActive(newDate);
    setIsYearMenuOpen(false);
    props.onSelectedYear && props.onSelectedYear();
  };

  const handleAction = (action: string) => {
    switch (action) {
      case 'next:month':
        return moveNextMonth();
      case 'prev:month':
        return movePrevMonth();
      default:
        return setIsYearMenuOpen(!isYearMenuOpen);
    }
  };

  React.useEffect(() => {
    const newActiveMoment = (props.active || moment(new Date())).startOf(
      'month'
    );
    setActive(newActiveMoment);
  }, [props.active]);

  return (
    <div onMouseLeave={(ev) => ev.stopPropagation()}>
      <DatepickerControls
        active={active}
        isYearMenuOpen={isYearMenuOpen}
        hasChanges={props.hasChanges}
        onChange={(action) => handleAction(action)}
      />
      {!isYearMenuOpen ? (
        <>
          <DatepickerCalendar
            active={active}
            min={min}
            max={max}
            start={start}
            end={end}
            hasFilters={true}
            hasChanges={props.hasChanges}
            isRangeSelector
            onRangeSelection={(
              newStart: moment.Moment,
              newEnd?: moment.Moment
            ) => handleChange(newStart, newEnd, true)}
          />
          {props.removeFilters ? null : (
            <DatepickerFilters
              filters={props.filters ? props.filters : FILTERS}
              active={activeFilter}
              hasChanges={props.hasChanges}
              onChange={(value) => handleFilter(value)}
            />
          )}
        </>
      ) : (
        <DatepickerYears
          onChange={(year) => handleYear(year)}
          currentYear={moment(active).year()}
          maxYear={max.year()}
          minYear={min.year()}
        />
      )}
    </div>
  );
};

export default DatepickerWithFilters;
